/* Profile-scope provenance + skipped-layer coverage guards (design F1).
 *
 * The backend stamps `result.skippedLayers` (layer keys a client profile
 * deliberately removed from the run) and `result.profile` (the merged
 * profile snapshot) into every run JSON. Before this module NOTHING in the
 * hub consumed either, so a profile-scoped run rendered as ordinary absence:
 * the hub would recommend "claim X" for a layer that was never actually
 * checked — the exact silent under-coverage CLAUDE.md's coverage discipline
 * exists to prevent.
 *
 * Two honest surfaces built on these fields (both PURE — no DOM, no globals,
 * first-parameter state like every other derive/*):
 *   1. scopeProvenanceLine() — the §0 ghost-meta line naming what the
 *      profile scoped out ("Scoped by client profile — claimable layer
 *      skipped · brief applied").
 *   2. isSkippedLayerOpportunity() — classifies an agent-emitted opportunity
 *      to the research layer that would have produced it, so buildState() can
 *      drop (never silently — the §0 line explains the skip) recommendations
 *      the run never verified.
 */

/** The four tailorable research layers (mirrors service/profiles.py
 * LAYER_KEYS). Kept terse for the provenance line ("claimable layer
 * skipped"). */
export const SCOPE_LAYER_LABEL: Record<string, string> = {
  owned: 'owned',
  claimable: 'claimable',
  community: 'community',
  media: 'media',
};

/** Minimal structural shape of a stored/merged profile snapshot — only the
 * fields the scope surfaces read. The full typed shape lives in
 * api/profiles.ts (ResearchProfileData); this avoids a derive → api import. */
export interface ScopeProfile {
  brief?: string | null;
  [k: string]: unknown;
}

const COMMUNITY_RE = /wikipedia|wikidata|wikimedia|wikispecies|reddit|quora|crunchbase/i;
const OWNED_SOCIAL_RE =
  /facebook|instagram|twitter|linkedin|youtube|tiktok|threads|pinterest|snapchat|weibo|telegram|mastodon|\bx\b/i;

/** Best-effort classification of an agent-emitted opportunity to the layer
 * that would have produced it. Deterministic and CONSERVATIVE: an
 * unclassifiable opportunity returns null (never suppressed). A `claim`
 * action is the canonical claimable-layer signal — the exact case design
 * named ("recommends claiming platforms that were deliberately skipped"). */
export function opportunityLayer(o: {
  action?: string | null;
  platform?: string | null;
}): string | null {
  const platform = (o.platform || '').trim();
  if (COMMUNITY_RE.test(platform)) return 'community';
  if (o.action === 'claim') return 'claimable';
  if (OWNED_SOCIAL_RE.test(platform)) return 'owned';
  if (o.action === 'create' || o.action === 'reactivate' || o.action === 'consolidate') {
    return 'owned';
  }
  return null;
}

/** True when an opportunity belongs to a layer the profile skipped — so the
 * hub must not present it as an actionable gap the run confirmed. */
export function isSkippedLayerOpportunity(
  o: { action?: string | null; platform?: string | null },
  skippedLayers: string[] | null | undefined,
): boolean {
  if (!skippedLayers || !skippedLayers.length) return false;
  const layer = opportunityLayer(o);
  return layer != null && skippedLayers.includes(layer);
}

/** The §0 scope-provenance line, or null when the run was unscoped (no
 * profile shaped it and nothing was skipped). Composed so it reads honestly
 * for every combination: skips, brief, or a bare profile. */
export function scopeProvenanceLine(
  profile: ScopeProfile | null | undefined,
  skippedLayers: string[] | null | undefined,
): string | null {
  const skipped = (Array.isArray(skippedLayers) ? skippedLayers : []).filter(
    (k): k is string => typeof k === 'string' && k.length > 0,
  );
  const hasProfile = !!profile && typeof profile === 'object';
  if (!skipped.length && !hasProfile) return null;

  const clauses: string[] = [];
  if (skipped.length) {
    const labels = skipped.map((k) => SCOPE_LAYER_LABEL[k] || k);
    clauses.push(`${labels.join(', ')} layer${skipped.length > 1 ? 's' : ''} skipped`);
  }
  if (hasProfile && typeof profile!.brief === 'string' && profile!.brief.trim()) {
    clauses.push('brief applied');
  }
  const base = 'Scoped by client profile';
  return clauses.length ? `${base} — ${clauses.join(' · ')}` : base;
}
